import { BoxProps, Box, Row } from "@hightouch/htui";
import { usePulseStaggerAnimation } from "./utils";
import { SIZES } from "src/ui/box";
import { chartBarRadius } from "src/components/charts/chart-utils";

const baseHeights = ["160px", "153px", "92px", "167px"];
const thinHeights = [
  "142px",
  "151px",
  "160px",
  "153px",
  "133px",
  "122px",
  "142px",
  "138px",
  "128px",
  "132px",
];
const percentageHeights = [
  "50%",
  "52%",
  "54%",
  "63%",
  "58%",
  "55%",
  "72%",
  "52%",
  "50%",
  "48%",
];

type ColumnChartLoaderProps = Omit<BoxProps, "maxW"> & {
  /**
   * Determines if column heights should be expressed as percentages of the parent container.
   * @default false
   */
  usePercentageHeights?: boolean;
  /**
   * The variant of the column chart loader.
   * - "thin": 64 columns with 8px gap
   * - "base": 4 columns with 1px gap
   *
   * @default "base"
   */
  variant?: "thin" | "base";
  /**
   * Use only to override the default number of columns for the variant.
   * @default undefined
   */
  columns?: number;
  maxW?: BoxProps["maxW"];
};

/**
 * Loader animation for column charts.
 * Staggered pulse effect.
 */
export const ColumnChartLoader = ({
  usePercentageHeights = false,
  columns,
  variant = "base",
  maxW = SIZES.chartLoader,
  ...props
}: ColumnChartLoaderProps) => {
  const isThin = variant === "thin";
  const { className, scope } = usePulseStaggerAnimation({
    staggerDelay: isThin ? 0.1 : undefined,
  });

  const numColumns = columns ?? (isThin ? 64 : 4);
  const gap = isThin ? 1 : 8;
  const borderTopRadius = isThin
    ? `${chartBarRadius.sm}px`
    : `${chartBarRadius.lg}px`;
  const heights = usePercentageHeights
    ? percentageHeights
    : isThin
      ? thinHeights
      : baseHeights;

  return (
    <Row
      ref={scope}
      align="end"
      boxSize="100%"
      gap={gap}
      justify="space-evenly"
      maxW={maxW}
      mx="auto"
      pb={4} // excess space where the chart keys live
      overflow="hidden"
      {...props}
    >
      {Array.from({ length: numColumns }).map((_, columnIndex) => (
        <ColumnItem
          key={`${columnIndex}`}
          className={className}
          height={heights[columnIndex % heights.length]}
          borderTopRadius={borderTopRadius}
        />
      ))}
    </Row>
  );
};

function ColumnItem({
  className,
  height,
  borderTopRadius,
}: Pick<BoxProps, "className" | "height" | "borderTopRadius">) {
  return (
    <Box
      className={className}
      boxSize="100%"
      bg="gray.200"
      borderTopRadius={borderTopRadius}
      flex={1}
      height={height}
    />
  );
}
